import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  StudentListWrapper, StudentHeader, HeaderContainer,
  SvgWrapper, StudentListItemWrapper, CheckboxHeader
} from './styled';
import { customSort } from './student-list-util';
import StudentListItem from './student-list-item';
import { Bolt, DownArrow, UpArrow } from '../../helpers/icon-dictionary';
import SvgIcon from '../svg-icon/svg-icon';
import {
  updateSortStudents,
  updateSortProps,
  selectGridCells
} from '../../containers/upf-assign/upf-assign-actions';
import { CHECKED, UNCHECKED } from '../checkbox/constants';
import { isAssessmentsReadOnly } from '../../helpers/utils';
import { useTracking } from '../../analytics';

const StudentList = ({ learningJourneyIds, i18n }) => {
  const props = useSelector(state => state.UPFAssignReducer);
  const studentsProgress = useSelector(state => state.UPFAssignReducer.studentsProgress);
  const dispatch = useDispatch();
  const [direction, setDirection] = useState('desc');
  const [students, setStudents] = useState([]);
  const [sortingProp, setSortingProp] = useState('direction');
  const { trackSelectAll } = useTracking();
  const { filteredStudents = [], sortDirection, isLoading, selectedStudentIds } = props;
  const studentLabel = i18n.t`Name`;
  const assignedLabel = i18n.t`Progress`;

  useEffect(() => {
    setStudents(customSort(filteredStudents, ['lastName', 'firstName'], sortDirection));
  }, [filteredStudents]);

  useEffect(() => {
    const updatedStudents = [];
    students.forEach((student) => {
      const assignment = studentsProgress && studentsProgress.find(ac => ac.userProfileId === student.id);
      const studentData = { ...student, showProgress: false };
      if (assignment) {
        studentData.showProgress = true;
        studentData.assignmentId = assignment.assignmentId;
        studentData.isReviewed = assignment.isReviewed;
        studentData.studentPct = assignment.studentPct;
      }
      updatedStudents.push(studentData);
    });
    setStudents(updatedStudents);
  }, [studentsProgress]);

  const sortHandler = () => {
    if (sortDirection === 'asc') {
      dispatch(updateSortProps('desc', ['lastName', 'firstName']));
    } else {
      dispatch(updateSortProps('asc', ['lastName', 'firstName']));
    }

    if (direction === 'asc') {
      setDirection('desc');
    } else {
      setDirection('asc');
    }

    const sortedList = customSort(students, ['lastName', 'firstName'], sortDirection === 'asc' ? 'desc' : 'asc');
    setStudents(sortedList);
    dispatch(updateSortStudents(sortedList));
    setSortingProp('direction');
  };

  const handleOnSelectStudent = (id, status) => {
    dispatch(selectGridCells(id, status, learningJourneyIds));
  };

  const handleSelectAll = () => {
    const isAllSelected = selectedStudentIds.length === students.length;
    trackSelectAll(!isAllSelected);
    if (isAllSelected) {
      students.forEach((student) => {
        handleOnSelectStudent(student.id, UNCHECKED);
      });
    } else {
      const unSelectedStudents = students.filter(student => !selectedStudentIds.includes(student.id));
      unSelectedStudents.forEach((student) => {
        handleOnSelectStudent(student.id, CHECKED);
      });
    }
  };

  const handleSortingArrow = (dir, isActive) => {
    const icon = dir === 'asc' ? <SvgIcon icon={DownArrow} /> : <SvgIcon icon={UpArrow} />;
    return <SvgWrapper status={isActive}>{icon}</SvgWrapper>;
  };

  if (isLoading) return <div />;
  return (
    <StudentListWrapper id="studentList">
      <HeaderContainer>
        <StudentHeader>
          <span
            className="title"
            role="button"
            tabIndex="-1"
            onClick={sortHandler}>
            {handleSortingArrow(sortDirection, sortingProp === 'direction')}
            <span className="label">{`${studentLabel} (${filteredStudents.length || 0})`}</span>
          </span>
          <span className="activities">{assignedLabel}</span>
        </StudentHeader>
        <CheckboxHeader>
          {!isAssessmentsReadOnly() && <button onClick={handleSelectAll}><SvgIcon icon={Bolt} /></button>}
        </CheckboxHeader>
      </HeaderContainer>
      <StudentListItemWrapper>
        {students.map(student => (<StudentListItem
          student={student}
          key={`_${student.id}`}
          checkboxHandler={handleOnSelectStudent}
          i18n={i18n} />))}
      </StudentListItemWrapper>
    </StudentListWrapper>
  );
};

StudentList.propTypes = {
  filteredStudents: PropTypes.array.isRequired,
  sortDirection: PropTypes.string,
  isLoading: PropTypes.bool,
  learningJourneyIds: PropTypes.array,
  i18n: PropTypes.object,
  selectedStudentIds: PropTypes.array
};

StudentList.defaultProps = {
  isLoading: false,
  filteredStudents: [],
  selectedStudentIds: [],
  studentsAssignmentStatus: [],
  learningJourneyIds: []
};

StudentList.whyDidYouRender = false;

export default StudentList;
